import { CountUp } from 'countup.js';
import { formatMoney } from '../form';
import { debounce, parseMoneyInput, clamp } from './_helpers';
import { fetchAddressDetails } from './_fetch-address-details';

function safeText(value) {
  return String(value ?? '').trim();
}

function normalizeBadSmartyUrl(str) {
  let s = safeText(str);
  if (!s) return s;

  const firstQ = s.indexOf('?');
  if (firstQ !== -1) {
    s = s.replaceAll(/\?(?=[a-zA-Z0-9_-]+=)/g, (m, offset) => (offset === firstQ ? m : '&'));
  }

  return s;
}

function parsePossiblyUnsafeUrl(raw) {
  const cleaned = normalizeBadSmartyUrl(raw);
  if (!cleaned) return null;

  try {
    return new URL(cleaned, globalThis.location.origin);
  } catch {
    try {
      return new URL(encodeURI(cleaned), globalThis.location.origin);
    } catch {
      return null;
    }
  }
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

/**
 * Eases a range input's value (and its paired number display) from its
 * current value to `toValue`, instead of snapping. Guards against
 * overlapping animations on the same element (e.g. a second address
 * lookup landing before the first finishes) via a per-element token.
 *
 * Resolves once the target value has actually landed, so callers that
 * need the final (not intermediate) value — e.g. pricing the offer —
 * can await it instead of racing the animation.
 */
function animateValue(sliderEl, renderFn, toValue, duration = 650) {
  const from = Number(sliderEl.value);
  const to = Number(toValue);

  const token = (sliderEl._animToken || 0) + 1;
  sliderEl._animToken = token;

  if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) {
    sliderEl.value = String(to);
    renderFn(to);
    return Promise.resolve();
  }

  const start = performance.now();

  return new Promise((resolve) => {
    function frame(now) {
      if (sliderEl._animToken !== token) {
        resolve(); // superseded by a newer animation on this element
        return;
      }

      const progress = Math.min((now - start) / duration, 1);
      const eased = easeOutCubic(progress);
      const current = from + (to - from) * eased;

      sliderEl.value = String(Math.round(current));
      renderFn(current);

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(frame);
  });
}

export function basicCalculatorInit(options) {
  if (!options || typeof options !== 'object') return;

  const { selectors, unlk, initSmarty, sendTrack, smartyNavigation, notices } = options;

  const ok =
    selectors &&
    unlk &&
    typeof initSmarty === 'function' &&
    typeof sendTrack === 'function' &&
    smartyNavigation &&
    notices;

  if (!ok) return;

  const rootEl = document.querySelector(selectors.root);
  if (!rootEl) return;

  const homeValueInput = rootEl.querySelector(selectors.homeValueInput);
  const homeValueSlider = rootEl.querySelector(selectors.homeValueSlider);
  const debtValueInput = rootEl.querySelector(selectors.debtValueInput);
  const debtValueSlider = rootEl.querySelector(selectors.debtValueSlider);
  const calculatorNotice = rootEl.querySelector(selectors.calculatorNotice);
  const addressNotice = rootEl.querySelector(selectors.addressNotice);
  const applyBtn = rootEl.querySelector(selectors.applyBtn);

  if (!homeValueInput || !homeValueSlider || !debtValueInput || !debtValueSlider || !applyBtn) {
    return;
  }

  const responseTarget = selectors.responseValueTarget;
  if (!responseTarget) return;

  const countUpTarget =
    typeof responseTarget === 'string' && responseTarget.startsWith('#')
      ? responseTarget.slice(1)
      : responseTarget;

  const responseValue = new CountUp(countUpTarget, 0, {
    formattingFn: (n) => formatMoney(Math.trunc(n)),
    duration: 0.6
  });

  let pendingDebtAdjustment = null;
  let lastSmartyAddress = null;
  // eslint-disable-next-line no-unused-vars
  let hasSmartyEstimates = false;
  let noEstimatesMode = false;
  let ctaLive = false;
  let offerController = null;
  let offerFrozen = false;

  const setNotice = (() => {
    const DUR = 260; // match CSS height transition

    // Per-type maps: key -> { el, timer }
    const store = {
      calculator: new Map(),
      address: new Map()
    };

    function forceReflow(el) {
      el.getBoundingClientRect();
    }

    function ensureInner(noticeEl) {
      let inner = noticeEl.querySelector('.notice-inner');
      if (!inner) {
        inner = document.createElement('div');
        inner.className = 'notice-inner';
        // move any server-rendered nodes inside once
        while (noticeEl.firstChild) inner.append(noticeEl.firstChild);
        noticeEl.append(inner);
      }

      return inner;
    }

    function setHeightPx(el, px) {
      el.style.height = `${Math.max(0, Math.ceil(px))}px`;
    }

    function measureOuterHeight(noticeEl) {
      const prev = noticeEl.style.height;
      noticeEl.style.height = 'auto';
      const h = noticeEl.scrollHeight;
      noticeEl.style.height = prev;
      return h;
    }

    // 2-phase open avoids snap-in
    function openToFit(noticeEl) {
      clearTimeout(noticeEl._closeT);
      clearTimeout(noticeEl._autoT);

      if (noticeEl.hidden) noticeEl.hidden = false;

      // prep collapsed state first
      noticeEl.classList.remove('is-visible');
      setHeightPx(noticeEl, 0);
      forceReflow(noticeEl);

      requestAnimationFrame(() => {
        noticeEl.classList.add('is-visible');
        setHeightPx(noticeEl, measureOuterHeight(noticeEl));

        noticeEl._autoT = setTimeout(() => {
          if (noticeEl.classList.contains('is-visible')) noticeEl.style.height = 'auto';
        }, DUR + 20);
      });
    }

    function resizeToFit(noticeEl) {
      if (noticeEl.hidden) return;
      if (!noticeEl.classList.contains('is-visible')) return;

      const current = noticeEl.getBoundingClientRect().height;
      setHeightPx(noticeEl, current);
      forceReflow(noticeEl);

      setHeightPx(noticeEl, measureOuterHeight(noticeEl));

      clearTimeout(noticeEl._autoT);
      noticeEl._autoT = setTimeout(() => {
        if (noticeEl.classList.contains('is-visible')) noticeEl.style.height = 'auto';
      }, DUR + 20);
    }

    function closeIfEmpty(noticeEl) {
      const inner = ensureInner(noticeEl);
      if (inner.children.length) return;

      const start = noticeEl.getBoundingClientRect().height;
      setHeightPx(noticeEl, start);
      forceReflow(noticeEl);

      noticeEl.classList.remove('is-visible');
      setHeightPx(noticeEl, 0);

      clearTimeout(noticeEl._closeT);
      noticeEl._closeT = setTimeout(() => {
        inner.innerHTML = '';
        noticeEl.hidden = true;
        noticeEl.style.height = '';
      }, DUR + 40);
    }

    function removeByKey(type, key, noticeEl) {
      const entry = store[type].get(key);
      if (!entry) return;

      clearTimeout(entry.timer);
      try {
        entry.el?.remove();
      } catch {}

      store[type].delete(key);

      const inner = ensureInner(noticeEl);
      requestAnimationFrame(() => {
        if (inner.children.length) resizeToFit(noticeEl);
        else closeIfEmpty(noticeEl);
      });
    }

    function upsert(type, noticeEl, msg, { id, html, durationMs }) {
      const inner = ensureInner(noticeEl);

      // IMPORTANT: stable key. If you don't pass id, it will fall back to msg text.
      const key = String(id || msg || '').trim();
      if (!key) return;

      // If already exists: update content (optional) + reset timer, no duplicate
      const existing = store[type].get(key);
      if (existing && existing.el && existing.el.isConnected) {
        if (msg) {
          if (html) existing.el.innerHTML = msg;
          else existing.el.textContent = msg;
        }

        clearTimeout(existing.timer);
        existing.timer = setTimeout(() => removeByKey(type, key, noticeEl), durationMs);

        openToFit(noticeEl);
        requestAnimationFrame(() => resizeToFit(noticeEl));
        return;
      }

      // Otherwise create a new item
      const item = document.createElement('div');
      item.className = 'notice-item';
      item.dataset.noticeId = key;
      if (html) item.innerHTML = msg;
      else item.textContent = msg;

      inner.append(item);

      const entry = {
        el: item,
        timer: setTimeout(() => removeByKey(type, key, noticeEl), durationMs)
      };

      store[type].set(key, entry);

      openToFit(noticeEl);
      requestAnimationFrame(() => resizeToFit(noticeEl));
    }

    // Public function signature you already use
    return (msg, type, { id, html = false, durationMs = 3500 } = {}) => {
      const noticeEl = type === 'address' ? addressNotice : calculatorNotice;
      if (!noticeEl) return;

      // Clear ALL notices in that container
      if (!msg) {
        // remove everything in the map for this type
        for (const key of store[type].keys()) removeByKey(type, key, noticeEl);
        store[type].clear();

        // ensure fully closed
        const inner = ensureInner(noticeEl);
        inner.innerHTML = '';
        closeIfEmpty(noticeEl);
        return;
      }

      upsert(type, noticeEl, msg, { id, html, durationMs });
    };
  })();

  function clearNotices() {
    setNotice('', 'calculator');
    setNotice('', 'address');
  }

  function startEstimateLoading() {
    rootEl.classList.add('is-fetching-estimate');
    setNotice("Fetching your home's estimated value…", 'calculator', {
      id: 'fetching-estimate',
      durationMs: 15_000
    });
  }

  function stopEstimateLoading() {
    rootEl.classList.remove('is-fetching-estimate');
    setNotice('', 'calculator');
  }

  function getHomeBounds() {
    return { min: Number(homeValueSlider.min), max: Number(homeValueSlider.max) };
  }

  function getDebtBounds() {
    return { min: Number(debtValueSlider.min), max: Number(debtValueSlider.max) };
  }

  function renderHome(value) {
    homeValueInput.value = formatMoney(Number(value));
  }

  function renderDebt(value) {
    debtValueInput.value = formatMoney(Number(value));
  }

  function syncDebtMax(homeRaw) {
    const home = Number(homeRaw);
    const currentDebt = Number(debtValueSlider.value);

    debtValueSlider.max = String(home);
    debtValueInput.max = String(home);

    if (currentDebt > home) {
      pendingDebtAdjustment = { from: currentDebt, to: home };
      debtValueSlider.value = String(home);
      renderDebt(home);
    }
  }

  function maybeShowDebtClampNotice() {
    if (pendingDebtAdjustment) {
      setNotice(
        notices.debtClampedText.replace('{value}', formatMoney(pendingDebtAdjustment.to)),
        'calculator',
        { id: 'debt-high' }
      );

      pendingDebtAdjustment = null;
    }
  }

  function cancelOffer() {
    try {
      if (offerController) offerController.abort();
    } catch {}

    offerController = null;
  }

  function fetchOffer(home, debt) {
    if (offerFrozen) return Promise.resolve(null);

    cancelOffer();
    offerController = new AbortController();

    const baseUrl = unlk.publicOffer;
    if (!baseUrl) return Promise.resolve(null);

    const params = new URLSearchParams({
      value: String(Math.trunc(home)),
      debt: String(Math.trunc(debt)),
      format: 'json'
    });

    return fetch(`${baseUrl}?${params.toString()}`, { signal: offerController.signal }).then(
      (res) => {
        if (!res.ok) throw new Error(`Offer request failed: ${res.status}`);
        return res.json();
      }
    );
  }

  function handleOfferResponse(data) {
    if (offerFrozen) return;
    if (!data) return;

    if (data.error) {
      responseValue.update(0);

      const msg = String(data.error_message || notices.offerEquityTooLowText || '').trim();
      if (msg)
        setNotice(msg, 'calculator', {
          id: 'equity-too-low'
        });

      return;
    }

    const maxValue = Number(data.max_value);
    if (!Number.isFinite(maxValue)) return;

    setNotice('', 'calculator');
    responseValue.update(Math.trunc(maxValue));
  }

  const updateOffer = debounce(() => {
    if (offerFrozen) return;

    const home = Number(homeValueSlider.value);
    const debt = Number(debtValueSlider.value);

    fetchOffer(home, debt)
      .then(handleOfferResponse)
      .catch((error) => {
        // eslint-disable-next-line no-console
        if (error?.name !== 'AbortError') console.error(error);
      });
  }, 350);

  function getCtaRaw() {
    return applyBtn.getAttribute('data-href') || applyBtn.dataset?.href || '';
  }

  function setCta(url) {
    if (!url) return;
    applyBtn.setAttribute('data-href', url);
    if (applyBtn.dataset) applyBtn.dataset.href = url;
  }

  function syncCtaToCurrentValues() {
    if (!ctaLive) return;

    const raw = getCtaRaw();
    const u = parsePossiblyUnsafeUrl(raw);
    if (!u) return; // don't invent a base

    u.searchParams.set('valueEstimated', String(Number(homeValueSlider.value)));
    u.searchParams.set('debtEstimated', String(Number(debtValueSlider.value)));

    if (lastSmartyAddress?.streetLineWithSecondary && lastSmartyAddress?.city) {
      u.searchParams.set('streetAddress', lastSmartyAddress.streetLineWithSecondary);
      u.searchParams.set('city', lastSmartyAddress.city);
      if (lastSmartyAddress.state) u.searchParams.set('state', lastSmartyAddress.state);
      if (lastSmartyAddress.zipcode) u.searchParams.set('zipcode', lastSmartyAddress.zipcode);
    }

    setCta(u.toString());
  }

  const syncCtaDebounced = debounce(syncCtaToCurrentValues, 150);

  function enterNoEstimatesMode() {
    hasSmartyEstimates = false;
    noEstimatesMode = true;
    offerFrozen = true;
    cancelOffer();

    applyBtn.textContent = selectors.smarty.buttonIdle;

    setNotice(notices.noEstimatesHtml, 'address', {
      id: 'no-estimate',
      html: true,
      durationMs: 10_000
    });

    const { min: homeMin } = getHomeBounds();
    const { min: debtMin } = getDebtBounds();

    homeValueSlider.value = String(homeMin);
    debtValueSlider.value = String(debtMin);
    syncDebtMax(homeMin);

    homeValueInput.value = formatMoney(0);
    debtValueInput.value = formatMoney(0);
    responseValue.update(0);

    syncCtaToCurrentValues();
  }

  function enterHasEstimatesMode({ homeEst, debtEst }) {
    hasSmartyEstimates = true;
    noEstimatesMode = false;

    offerFrozen = false;

    applyBtn.textContent = selectors.smarty.buttonIdle;

    const hb = getHomeBounds();
    const homeNext = clamp(Number(homeEst), hb.min, hb.max);

    syncDebtMax(homeNext);
    pendingDebtAdjustment = null; // syncDebtMax may flag a clamp against the stale debt value; this is a fresh estimate, not a user-driven clamp.

    const db = getDebtBounds();
    const debtMax = Math.min(Number(homeNext), Number(debtValueSlider.max));
    const debtNext = clamp(Number(debtEst), db.min, debtMax);

    Promise.all([
      animateValue(homeValueSlider, renderHome, homeNext),
      animateValue(debtValueSlider, renderDebt, debtNext)
    ]).then(() => {
      updateOffer();
      syncCtaToCurrentValues();
    });
  }

  function wakeFromNoEstimatesModeIfNeeded() {
    if (!noEstimatesMode) return;

    offerFrozen = false;
    noEstimatesMode = false;

    renderHome(homeValueSlider.value);
    renderDebt(debtValueSlider.value);
    clearNotices();

    updateOffer();
    syncCtaToCurrentValues();
  }

  renderHome(homeValueSlider.value);
  renderDebt(debtValueSlider.value);
  syncDebtMax(homeValueSlider.value);
  updateOffer();

  homeValueSlider.addEventListener('input', function () {
    wakeFromNoEstimatesModeIfNeeded();

    renderHome(this.value);
    syncDebtMax(this.value);

    clearNotices();

    syncCtaDebounced();
  });

  homeValueSlider.addEventListener('change', () => {
    maybeShowDebtClampNotice();
    updateOffer();
    syncCtaToCurrentValues();
  });

  debtValueSlider.addEventListener('input', function () {
    wakeFromNoEstimatesModeIfNeeded();
    renderDebt(this.value);
    pendingDebtAdjustment = null;
    clearNotices();
    syncCtaDebounced();
  });

  debtValueSlider.addEventListener('change', () => {
    updateOffer();
    syncCtaToCurrentValues();
  });

  homeValueInput.addEventListener('input', function () {
    wakeFromNoEstimatesModeIfNeeded();
    const { min, max } = getHomeBounds();
    const parsed = parseMoneyInput(this.value);
    const next = clamp(parsed, min, max);
    homeValueSlider.value = String(next);
    syncDebtMax(next);
    pendingDebtAdjustment = null;
    clearNotices();
    updateOffer();
    syncCtaDebounced();
  });

  homeValueInput.addEventListener('change', function () {
    wakeFromNoEstimatesModeIfNeeded();

    const { min, max } = getHomeBounds();
    const parsed = parseMoneyInput(this.value);
    const next = clamp(parsed, min, max);

    homeValueSlider.value = String(next);
    renderHome(next);

    syncDebtMax(next);
    maybeShowDebtClampNotice();
    updateOffer();
    syncCtaToCurrentValues();
  });

  debtValueInput.addEventListener('input', function () {
    wakeFromNoEstimatesModeIfNeeded();

    const { min } = getDebtBounds();
    const home = Number(homeValueSlider.value);
    const parsed = parseMoneyInput(this.value);
    const next = clamp(parsed, min, home);

    debtValueSlider.value = String(next);
    pendingDebtAdjustment = null;

    clearNotices();
    updateOffer();
    syncCtaDebounced();
  });

  debtValueInput.addEventListener('change', function () {
    wakeFromNoEstimatesModeIfNeeded();

    const { min } = getDebtBounds();
    const home = Number(homeValueSlider.value);
    const parsed = parseMoneyInput(this.value);
    const next = clamp(parsed, min, home);

    debtValueSlider.value = String(next);
    renderDebt(next);

    pendingDebtAdjustment = null;

    setNotice('', 'calculator');
    setNotice('', 'address');

    updateOffer();
    syncCtaToCurrentValues();
  });

  if (!unlk.smartyKey) return;

  initSmarty({
    selectors: {
      addressInput: selectors.smarty.addressInput,
      inputGroup: selectors.smarty.inputGroup,
      suggestionBox: selectors.smarty.suggestionBox,
      qualButton: selectors.smarty.qualButton,
      invalidScope: selectors.smarty.invalidScope
    },

    navigation: smartyNavigation,
    track: sendTrack,

    smartyKey: unlk.smartyKey,
    activeStates: unlk.activeStates,
    estimateUrl: unlk.estimateUrl,
    smartyQualUrl: unlk.smartyQualUrl,
    smartyNonQualUrl: unlk.smartyNonQualUrl,

    labels: { buttonIdle: selectors.smarty.buttonIdle },
    buttonLabelMode: 'cta',

    async onAddress(address) {
      ctaLive = true;
      lastSmartyAddress = address;

      startEstimateLoading();

      try {
        const details = await fetchAddressDetails({
          streetLineWithSecondary: address.streetLineWithSecondary,
          city: address.city,
          state: address.state,
          zipcode: address.zipcode,
          estimateUrl: unlk.estimateUrl
        });

        const homeEst = Number(details?.estimated_value);
        const debtEst = Number(details?.estimated_debt);
        const gotAnyEstimate = Number.isFinite(homeEst) || Number.isFinite(debtEst);

        if (!details || !gotAnyEstimate || homeEst <= 0) {
          enterNoEstimatesMode();
          return;
        }

        enterHasEstimatesMode({ homeEst, debtEst: Number.isFinite(debtEst) ? debtEst : 0 });
      } catch {
        enterNoEstimatesMode();
      } finally {
        stopEstimateLoading();
      }
    }
  });
}

globalThis.unlk = globalThis.unlk || {};
globalThis.unlk.basicCalculatorInit = basicCalculatorInit;

